/**
 * Embedded シェル（モバイル WebView 用・汎用）
 *
 * ?embedded=1 でネイティブから idToken を postMessage 受領し、アプリのヘッダ/サイドバー無しで
 * children（各レポート）を描画する。ProtectedRoute の外に置き、Amplifyログインは要求しない。
 * 認証は axiosClient が注入トークンを使用（embeddedAuth）。
 */
import React, { useEffect, useState } from 'react';
import {
  attachEmbeddedTokenListener,
  waitForToken,
  getInjectedToken,
  getEmbeddedLang,
} from '../../shared/api/embeddedAuth';
import i18n from '../../i18n/config';

// 対応言語（languageUtils / questionnaire.dto と一致させる）。'preferredLanguage' は
// getStoredLanguage(languageUtils) が読むキーと同一にすること。
const SUPPORTED_LANGS = ['ja', 'en', 'id', 'mn', 'ne', 'vi', 'ar', 'bn', 'zh', 'es'];
const LANGUAGE_STORAGE_KEY = 'preferredLanguage';

export const EmbeddedShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState<boolean>(getInjectedToken() != null);

  // ネイティブから ?lang= で渡された表示言語を適用（画面文言=i18n、設問テキスト=getStoredLanguage）。
  useEffect(() => {
    const lang = getEmbeddedLang();
    if (lang && SUPPORTED_LANGS.includes(lang)) {
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      } catch {
        /* ignore */
      }
      void i18n.changeLanguage(lang);
    }
  }, []);

  useEffect(() => {
    // EMBED_READY をネイティブへ送り、SET_TOKENS(idToken) を受信開始
    const detach = attachEmbeddedTokenListener();
    let cancelled = false;
    if (getInjectedToken()) {
      setReady(true);
    } else {
      // トークン到達まで待つ（timeout でも描画。未取得なら初回APIが401→自動リフレッシュで回復）
      waitForToken(15000).then(() => {
        if (!cancelled) setReady(true);
      });
    }
    return () => {
      cancelled = true;
      detach();
    };
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: '#6b7280',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        読み込み中…
      </div>
    );
  }

  return <div style={{ minHeight: '100vh', background: '#fff' }}>{children}</div>;
};

export default EmbeddedShell;
